import { Search } from '@mui/icons-material';
import { Box, Checkbox, Divider, FormControlLabel, InputAdornment, Stack, SxProps, TextField, Theme, Typography, useTheme } from '@mui/material';
import { useState, useRef, useEffect, useMemo } from 'react';
import merge from 'lodash.merge';
import { FixedSizeList } from 'react-window';

export interface CheckboxSelectProps {
    placeholder: string;
    options: NestedItem[];
    selections?: string[];
    header?: string;
    maxItemsOnView?: number;
    menuItemSize?: number;
    showCount?: boolean;
    showSelectAll?: boolean;
    placementTop?: boolean;
    disabled?: boolean;
    searchDelay?: number;
    sx?: {
        container?: SxProps<Theme>;
        button?: SxProps<Theme>;
        menu?: SxProps<Theme>;
        text?: SxProps<Theme>;
        checkbox?: SxProps<Theme>;
    };
    onChange?: (selectedOptions: string[]) => void;
}

export interface CheckboxItem {
    itemId: string;
    parentId?: string;
    groupSize: number;
    level: number;
    label: string;
    value?: string;
    isGroupHeader: boolean;
}

export type NestedItem = {
    label: string;
    value?: string;
    children?: NestedItem[];
};

const useCheckboxSelectStyles = (theme: Theme): { [key: string]: any } => ({
    textField: {
        borderRadius: '6px',
        backgroundColor: theme.palette.background.default,
        borderColor: theme.palette.border.main,
        color: theme.palette.text.primary,
        ':hover': {
            backgroundColor: theme.palette.background.paper,
            borderColor: theme.palette.border.main
        },
        '& .MuiOutlinedInput-root': {
            height: '36px',
            fontSize: '14px',
            padding: '0 8px'
        },
        '&.Mui-disabled': {
            color: '#8D94A0'
        }
    },
    popUpContainer: { position: 'relative', zIndex: '10', marginTop: '5px' },
    popUpMenu: {
        border: 1,
        minWidth: '100%',
        position: 'absolute',
        overflowY: 'scroll',
        backgroundColor: theme.palette.background.default,
        borderColor: theme.palette.border.input,
        borderRadius: '6px',
        padding: '6px',
        gap: '8px'
    },
    text: { fontSize: '14px' },
    checkboxLabel: { margin: '0', gap: '12px' },
    checkbox: { padding: '0' },
    divider: { width: '100%', marginBottom: '6px' }
});

const aggregateRepetitions = (key: string, value: string, repetitionsMap: Map<string, string[]>) => {
    const values = repetitionsMap.get(key) || [];
    values.push(value);

    if (values.length === 1) {
        repetitionsMap.set(key, values);
    }
};

const getFlattenOptions = (args: {
    nestedArray: NestedItem[];
    level: number;
    parent?: CheckboxItem;
    repetitionsMap?: Map<string, string[]>;
}): CheckboxItem[] => {
    const { nestedArray, level = 0, parent, repetitionsMap } = args;

    let groupCount = 0;
    const flattenOptions: CheckboxItem[] = [];

    for (const item of nestedArray) {
        groupCount++;
        const currentId = parent?.itemId ? `${parent.itemId}.${groupCount}` : `${groupCount}`;

        const hasChildren = Array.isArray(item.children) && item.children.length > 0;

        const newItem: CheckboxItem = {
            itemId: currentId,
            parentId: parent?.itemId,
            groupSize: 0,
            level,
            label: item.label,
            value: item.value,
            isGroupHeader: hasChildren || !item.value
        };

        if (repetitionsMap && item.value) aggregateRepetitions(item.value, currentId, repetitionsMap);
        flattenOptions.push(newItem);

        if (hasChildren) {
            const children = getFlattenOptions({ nestedArray: item.children!, level: level + 1, parent: newItem, repetitionsMap: repetitionsMap });
            flattenOptions.push(...children);
        }
    }

    if (parent) parent.groupSize = groupCount;

    return flattenOptions;
};

export const CheckboxSelect = (props: CheckboxSelectProps) => {
    const theme = useTheme();
    const styles = useCheckboxSelectStyles(theme);

    const {
        placeholder,
        options,
        selections,
        header,
        maxItemsOnView = 8,
        menuItemSize = 32,
        showCount = false,
        showSelectAll = false,
        placementTop = false,
        disabled = false,
        searchDelay = 300,
        sx,
        onChange
    } = props;

    const [searchValue, setSearchValue] = useState('');
    const [showSubMenu, setShowSubMenu] = useState(false);
    const [timer, setTimer] = useState<number | undefined>(undefined);
    const [selectedOptions, setSelectedOptions] = useState<Map<string, string>>(new Map<string, string>());

    const { flatOptions, repetitionsMap, flatOptionsMap } = useMemo(() => {
        const repetitionsMap = new Map<string, string[]>();
        const flatOptions = getFlattenOptions({ nestedArray: options, level: 0, repetitionsMap: repetitionsMap });

        const flatOptionsMap = new Map<string, CheckboxItem>();
        flatOptions.forEach(option => flatOptionsMap.set(option.itemId, option));

        return { flatOptions, repetitionsMap, flatOptionsMap };
    }, [options]);

    const [filteredOptions, setFilteredOptions] = useState<CheckboxItem[]>([]);

    const menuHeight = Math.min(filteredOptions.length, maxItemsOnView) * menuItemSize;

    const groupSelectionsRef = useRef<Map<string, number>>(new Map<string, number>());
    const skipNextUpdateSelections = useRef(false);

    const menuRef = useRef<HTMLDivElement | null>(null);

    const isAllSelected = selectedOptions.size >= flatOptionsMap.size;

    useEffect(() => {
        setFilteredOptions(flatOptions);

        const handleClickOutside = (event: Event) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setFilteredOptions(flatOptions);
                setShowSubMenu(false);
                setSearchValue('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [flatOptions]);

    useEffect(() => {
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [timer]);

    useEffect(() => {
        if (!selections) return;
        if (skipNextUpdateSelections.current) {
            skipNextUpdateSelections.current = false;
            return;
        }

        const updatedSelections = new Map<string, string>();
        groupSelectionsRef.current = new Map<string, number>();

        for (const value of selections) {
            const existingItem = flatOptions.find(option => option.value === value);
            if (existingItem) checkItem(existingItem, updatedSelections);
        }

        setSelectedOptions(updatedSelections);
    }, [selections]);

    const handleSelectAll = () => {
        setSelectedOptions(prevSelectedOptions => {
            const newOptions = new Map(prevSelectedOptions);

            if (isAllSelected) {
                flatOptions.forEach(item => {
                    if (selectedOptions.has(item.itemId)) {
                        uncheckItem(item, newOptions);
                    }
                });
            } else {
                flatOptions.forEach(item => {
                    if (!selectedOptions.has(item.itemId)) {
                        checkItem(item, newOptions);
                    }
                });
            }

            onChange?.(
                Array.from(
                    new Set(
                        flatOptions
                            .filter(option => !option.isGroupHeader && option.value && newOptions.has(option.itemId))
                            .map(option => option.value!)
                    )
                )
            );

            return newOptions;
        });
    };

    const handleCheck = (item: CheckboxItem) => {
        setSelectedOptions(prevSelectedOptions => {
            const newOptions = new Map(prevSelectedOptions);

            if (newOptions.has(item.itemId)) uncheckItem(item, newOptions);
            else checkItem(item, newOptions);

            skipNextUpdateSelections.current = true;
            onChange?.(
                Array.from(
                    new Set(
                        flatOptions
                            .filter(option => !option.isGroupHeader && option.value && newOptions.has(option.itemId))
                            .map(option => option.value!)
                    )
                )
            );
            return newOptions;
        });
    };

    const updateGroupCount = (headerId: string, addValue: number, activeItems: Map<string, string>) => {
        const item = flatOptionsMap.get(headerId);
        const parent = flatOptionsMap.get(item?.parentId ?? '');

        if (!groupSelectionsRef.current || !item) return;

        const newCount = flatOptions.filter(item => item.parentId == headerId && activeItems.has(item.itemId)).length;
        groupSelectionsRef.current?.set(headerId, newCount);

        // Can be added directly to the Map because header never has a value and therefore cannot have duplicates
        if (newCount === item.groupSize && addValue > 0) {
            activeItems.set(item.itemId, item.label);
            if (item.parentId) updateGroupCount(item.parentId, 1, activeItems);
        } else if (newCount === item.groupSize - 1 && addValue < 0) {
            activeItems.delete(item.itemId);
            if (item.parentId) updateGroupCount(item.parentId, -1, activeItems);
        }
    };

    const addAllChildren = (parent: CheckboxItem, activeItems: Map<string, string>) => {
        const children = flatOptions.filter(item => item.parentId === parent.itemId);

        children.forEach(child => {
            checkItem(child, activeItems);
        });
    };

    const removeAllChildren = (parent: CheckboxItem, activeItems: Map<string, string>) => {
        const children = flatOptions.filter(item => item.parentId === parent.itemId);

        children.forEach(child => {
            uncheckItem(child, activeItems);
        });
    };

    const checkItem = (item: CheckboxItem, activeItems: Map<string, string>) => {
        if (activeItems.has(item.itemId)) return;

        const duplicates = getAllDuplicates(item);

        duplicates.forEach(duplicate => {
            if (activeItems.has(duplicate.itemId)) return;

            activeItems.set(duplicate.itemId, duplicate.label);
            if (duplicate.parentId) updateGroupCount(duplicate.parentId, 1, activeItems);
            if (duplicate.isGroupHeader) addAllChildren(duplicate, activeItems);
        });
    };

    const uncheckItem = (item: CheckboxItem, activeItems: Map<string, string>) => {
        if (!activeItems.has(item.itemId)) return;
        const duplicates = getAllDuplicates(item);

        duplicates.forEach(duplicate => {
            if (!activeItems.has(duplicate.itemId)) return;

            activeItems.delete(duplicate.itemId);
            if (duplicate.isGroupHeader) removeAllChildren(duplicate, activeItems);
            if (duplicate.parentId) updateGroupCount(duplicate.parentId, -1, activeItems);
        });
    };

    const getAllDuplicates = (item: CheckboxItem): CheckboxItem[] => {
        let repeatedItems = [item];

        // only items with the same value count as duplicates
        if (item.value) {
            const repetitionsList = repetitionsMap.get(item.value);
            if (repetitionsList && repetitionsList.length > 0) {
                repetitionsList.forEach(repeatedId => {
                    const repeatedItem = flatOptionsMap.get(repeatedId);
                    if (repeatedItem) repeatedItems.push(repeatedItem);
                });
            }
        }
        return repeatedItems;
    };

    const addParentBefore = (child: CheckboxItem, list: CheckboxItem[]) => {
        const parent = flatOptionsMap.get(child.parentId ?? '');
        if (!parent || list.includes(parent)) return;

        addParentBefore(parent, list);
        list.push(parent);
    };

    const onSearch = (text: string) => {
        setSearchValue(text);

        if (timer) clearTimeout(timer);

        const newTimer = setTimeout(() => {
            handleSearch(text);
        }, searchDelay);

        setTimer(newTimer);
    };

    const handleSearch = (text: string) => {
        if (!text) {
            setFilteredOptions(flatOptions);
            return;
        }

        const searchString = text.toLowerCase();
        let matches: CheckboxItem[] = [];

        for (let i = 0; i < flatOptions.length; i++) {
            const currentOption = flatOptions[i];

            if (currentOption.label.toLowerCase().includes(searchString)) {
                addParentBefore(currentOption, matches);
                matches.push(currentOption);

                if (currentOption.isGroupHeader) {
                    while (i < flatOptions.length - 1 && flatOptions[i + 1].level > currentOption.level) {
                        matches.push(flatOptions[i + 1]);
                        i++;
                    }
                }
            }
        }

        setFilteredOptions(matches);
    };

    // count selected non repeated values excluding headers
    const countSelections = () => {
        return new Set(
            selectedOptions
                .keys()
                .toArray()
                .filter(key => !!flatOptionsMap.get(key)?.value)
                .map(key => flatOptionsMap.get(key)?.value)
        ).size;
    };

    return (
        <Stack sx={sx?.container} ref={menuRef}>
            <TextField
                variant="outlined"
                value={showSubMenu ? searchValue : `${placeholder} ${showCount ? `(${countSelections()})` : ''}`}
                disabled={disabled}
                sx={merge(styles.textField, sx?.button)}
                onChange={e => onSearch(e.target.value)}
                onClick={() => setShowSubMenu(true)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search sx={{ color: disabled ? theme.palette.text.disabled : theme.palette.text.primary }} />
                        </InputAdornment>
                    )
                }}
            />

            {showSubMenu && (
                <Box sx={styles.popUpContainer}>
                    <Stack sx={merge({ ...styles.popUpMenu, transform: placementTop ? 'translateY(calc(-100% - 46px))' : undefined }, sx?.menu)}>
                        {header && <Typography sx={merge(styles.text, sx?.text)}>{header}</Typography>}

                        {showSelectAll && (
                            <>
                                <FormControlLabel
                                    componentsProps={{ typography: merge(styles.text, sx?.text) }}
                                    sx={styles.checkboxLabel}
                                    control={<Checkbox checked={isAllSelected} sx={styles.checkbox} onChange={handleSelectAll} />}
                                    label="Select All"
                                />
                                <Divider style={styles.divider} />
                            </>
                        )}

                        <FixedSizeList height={menuHeight} itemCount={filteredOptions.length} itemSize={menuItemSize} width={'100%'}>
                            {({ index, style }) => {
                                const option = filteredOptions[index];
                                return (
                                    <div style={style} key={option.value}>
                                        <FormControlLabel
                                            key={option.itemId}
                                            componentsProps={{
                                                typography: merge(
                                                    {
                                                        ...styles.text,
                                                        fontWeight: option.isGroupHeader ? 'bold' : 'initial'
                                                    },
                                                    sx?.text
                                                )
                                            }}
                                            sx={{
                                                ...styles.checkboxLabel,
                                                paddingLeft: `calc(${option.level} * 14px)`
                                            }}
                                            control={
                                                <Checkbox
                                                    sx={merge(styles.checkbox, sx?.checkbox)}
                                                    checked={selectedOptions.has(option.itemId)}
                                                    onChange={() => handleCheck(option)}
                                                />
                                            }
                                            label={option.label}
                                        />
                                    </div>
                                );
                            }}
                        </FixedSizeList>

                        {!filteredOptions.length && <Typography sx={merge(styles.text, sx?.text)}>Not found</Typography>}
                    </Stack>
                </Box>
            )}
        </Stack>
    );
};
